/**
 * useBranchContext - one branch's counts and freshness, read from the TPIN's
 * `/branches` list (TL ruling Q1, option a; ADR-005 3.4 retires the per-branch
 * summary read from the SPA).
 *
 * S-BRANCH, S3 and S4 all use it, so there is one mechanism: reuse the row the
 * overview already loaded for this scope, or walk the list page by page until
 * the row is found. Nothing here schedules a read of its own (PD-33): the
 * walk is driven by the lookup state, and a refresh is an explicit call that
 * re-reads this TPIN's branch list only.
 *
 * An unusable range is the empty string `from`: no real date is empty, so no
 * read is made for it.
 */

import { useEffect } from 'react'
import { branchLookup, lookupRefreshing, type BranchLookup } from '@domain/tpinBranch'
import { branchesFor } from '@domain/tpinDash'
import { useTpinOverviewStore } from '@stores/tpinOverviewStore'

export interface BranchContext {
  readonly lookup: BranchLookup
  /** True while an explicit refresh is re-reading a row that is already on screen. */
  readonly refreshing: boolean
  /** Explicit refresh: re-reads this TPIN's `/branches` only. */
  readonly refresh: () => void
  /** Re-reads the first page after a failed read. */
  readonly retry: () => void
  /** Reads the next page of the walk (also the retry of a stalled walk). */
  readonly loadMore: () => void
}

export function useBranchContext(tin: string, dbName: string, from: string, to: string): BranchContext {
  const view = useTpinOverviewStore((s) => branchesFor(s, tin, from, to))
  const openBranches = useTpinOverviewStore((s) => s.openBranches)
  const cancel = useTpinOverviewStore((s) => s.cancel)
  const refresh = useTpinOverviewStore((s) => s.refreshBranches)
  const retry = useTpinOverviewStore((s) => s.retryBranches)
  const loadMore = useTpinOverviewStore((s) => s.loadMore)

  useEffect(() => {
    if (from === '') return
    openBranches(tin, { from, to })
    return cancel
  }, [tin, from, to, openBranches, cancel])

  const lookup = branchLookup(view, dbName)

  // Cold deep link: the row is not loaded yet and more pages exist - read the next one.
  // The lookup leaves `advance` while a page is in flight and returns to it only after
  // the answer lands, so the effect fires once per page.
  useEffect(() => {
    if (lookup.kind === 'advance') loadMore()
  }, [lookup.kind, loadMore])

  return { lookup, refreshing: lookupRefreshing(lookup), refresh, retry, loadMore }
}
